/**
 * Gallery Block - Filtering, Layout Pattern, and Lightbox
 */

/**
 * Layout pattern: each entry = { span, aspectRatio }
 *   span        – CSS grid-column span (out of 12)
 *   aspectRatio – CSS aspect-ratio value for the image
 *
 * The pattern repeats. Rows are calculated automatically:
 * a new row starts whenever the cumulative span reaches 12.
 *
 * Current pattern:
 *   Row 1: 1 full-width image  (1200/584 ≈ 2.05:1)
 *   Row 2: 2 half-width images (1:1)
 *   Row 3: 3 third-width images (1:1)
 */
const GALLERY_PATTERN = [
	{ span: 12, aspectRatio: '1200 / 584' },
	{ span: 6,  aspectRatio: '1 / 1' },
	{ span: 6,  aspectRatio: '1 / 1' },
	{ span: 4,  aspectRatio: '1 / 1' },
	{ span: 4,  aspectRatio: '1 / 1' },
	{ span: 4,  aspectRatio: '1 / 1' },
];

/** Animation timing */
const ANIM = {
	rowDelay: 0.15,
	itemDelay: 0.1,
};

import {
	closeFilterDropdown,
	ensureFilterFlyoutRoot,
	getFilterPanel,
	getOpenFilterGroup,
	openFilterDropdownForGroup,
	repositionOpenFilterDropdown,
} from './gallery-filter-flyout.js';

/**
 * @returns {boolean}
 */
function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

class GalleryBlock {
	constructor(blockElement) {
		this.block = blockElement;
		this.blockId = blockElement.dataset.blockId;
		this.grid = blockElement.querySelector('.gallery-grid');
		this.filtersContainer = blockElement.querySelector('.gallery-filters');
		this.items = blockElement.querySelectorAll('.gallery-item');
		this.lightbox = blockElement.querySelector('.gallery-lightbox');
		
		const dataScript = blockElement.querySelector('.gallery-data');
		this.galleryData = dataScript ? JSON.parse(dataScript.textContent) : { images: [] };
		
		this.currentFilter = 'all';
		this.currentIndex = 0;
		this.visibleImages = [];
		
		this.init();
	}

	init() {
		this.updateVisibleImages();
		this.applyPattern();
		this.attachFilterListeners();
		this.attachGalleryListeners();
		this.attachLightboxListeners();
		this.attachKeyboardListeners();
		this.observeEntrance();
	}

	/**
	 * Apply the repeating layout pattern (span + aspect ratio + animation delay)
	 */
	applyPattern() {
		let patternIndex = 0;
		let colSum = 0;
		let row = 0;
		let indexInRow = 0;

		this.items.forEach(item => {
			if (item.classList.contains('is-hidden') || item.style.display === 'none') {
				return;
			}

			const entry = GALLERY_PATTERN[patternIndex % GALLERY_PATTERN.length];
			item.style.gridColumn = `span ${entry.span}`;
			item.style.setProperty('--gallery-aspect-ratio', entry.aspectRatio);

			const delay = row * ANIM.rowDelay + indexInRow * ANIM.itemDelay;
			item.style.setProperty('--anim-delay', `${delay}s`);

			colSum += entry.span;
			indexInRow++;
			if (colSum >= 12) {
				colSum = 0;
				row++;
				indexInRow = 0;
			}

			patternIndex++;
		});
	}

	/**
	 * Show grid items (entrance animation or instant reveal).
	 */
	revealGrid() {
		if (!this.grid) {
			return;
		}

		this.clearEntranceFallback();
		this.grid.classList.add('is-revealed');

		if (this.entranceObserver) {
			this.entranceObserver.disconnect();
			this.entranceObserver = null;
		}
	}

	/**
	 * Safety net when IntersectionObserver never fires (zero-height grid, bfcache, etc.).
	 */
	clearEntranceFallback() {
		if (this.entranceFallbackTimer) {
			clearTimeout(this.entranceFallbackTimer);
			this.entranceFallbackTimer = null;
		}
	}

	/**
	 * @returns {number}
	 */
	getViewportHeight() {
		return window.visualViewport?.height ?? window.innerHeight;
	}

	/**
	 * @param {Element} element
	 * @returns {boolean}
	 */
	isElementPartiallyVisible(element) {
		const rect = element.getBoundingClientRect();
		const viewportHeight = this.getViewportHeight();

		return rect.top < viewportHeight && rect.bottom > 0;
	}

	/**
	 * @returns {boolean}
	 */
	isGridInViewport() {
		if (!this.grid) {
			return false;
		}

		return this.isElementPartiallyVisible(this.grid);
	}

	/**
	 * True when any visible tile intersects the viewport (handles tall grids on small screens).
	 *
	 * @returns {boolean}
	 */
	isAnyGalleryItemInViewport() {
		return Array.from(this.items).some((item) => {
			if (item.classList.contains('is-hidden') || item.style.display === 'none') {
				return false;
			}

			return this.isElementPartiallyVisible(item);
		});
	}

	/**
	 * Re-check after layout/images settle — initial rect can be wrong at script run time.
	 *
	 * @param {() => void} tryReveal
	 */
	scheduleEntranceChecks(tryReveal) {
		requestAnimationFrame(() => {
			requestAnimationFrame(tryReveal);
		});

		window.setTimeout(tryReveal, 100);
		window.setTimeout(tryReveal, 400);

		this.grid.querySelectorAll('.gallery-item__image').forEach((img) => {
			if (img.complete) {
				return;
			}

			img.addEventListener('load', tryReveal, { once: true });
		});
	}

	/**
	 * Observe grid + individual tiles so partly visible items trigger reveal on mobile.
	 *
	 * @param {() => void} reveal
	 */
	setupEntranceObserver(reveal) {
		const isMobile = window.matchMedia('(max-width: 768px)').matches;
		const rootMargin = isMobile ? '0px 0px 25% 0px' : '100px 0px';

		this.entranceObserver = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				reveal();
			}
		}, { threshold: 0, rootMargin });

		this.entranceObserver.observe(this.grid);

		this.items.forEach((item) => {
			if (!item.classList.contains('is-hidden') && item.style.display !== 'none') {
				this.entranceObserver.observe(item);
			}
		});
	}

	/**
	 * Observe the gallery grid entering the viewport to trigger entrance animation
	 */
	observeEntrance() {
		if (!this.grid) {
			return;
		}

		if (prefersReducedMotion()) {
			return;
		}

		this.grid.classList.add('is-entering');

		const reveal = () => this.revealGrid();
		const tryReveal = () => {
			if (this.grid.classList.contains('is-revealed')) {
				return;
			}

			if (this.isAnyGalleryItemInViewport() || this.isGridInViewport()) {
				reveal();
			}
		};

		this.scheduleEntranceChecks(tryReveal);
		this.setupEntranceObserver(reveal);

		tryReveal();

		// Fallback: never leave items hidden if every check misses
		this.entranceFallbackTimer = window.setTimeout(reveal, 1500);
	}

	/**
	 * Update the list of currently visible images based on active filter
	 */
	updateVisibleImages() {
		this.visibleImages = [];
		this.items.forEach((item, index) => {
			const itemCategories = item.dataset.categories || 'all';
			const categoriesArray = itemCategories.split(',').map(cat => cat.trim());
			const shouldShow = this.currentFilter === 'all' || categoriesArray.includes(this.currentFilter);
			
			if (shouldShow) {
				this.visibleImages.push(index);
			}
		});
	}

	/**
	 * Attach filter button listeners (delegated for hierarchical groups).
	 */
	attachFilterListeners() {
		if (!this.filtersContainer) {
			return;
		}

		this.flyoutRoot = ensureFilterFlyoutRoot(this.blockId);

		this.onFilterClick = (event) => {
			const filterBtn = event.target.closest('.gallery-filter-btn');
			if (!filterBtn) {
				return;
			}

			const inFlyout = this.flyoutRoot?.contains(filterBtn);
			const inToolbar = this.filtersContainer.contains(filterBtn);

			if (!inFlyout && !inToolbar) {
				return;
			}

			const category = filterBtn.dataset.category || 'all';
			this.setActiveFilter(filterBtn);
			this.filterGallery(category);
		};

		this.filtersContainer.addEventListener('click', this.onFilterClick);
		this.flyoutRoot.addEventListener('click', this.onFilterClick);

		this.filtersContainer.addEventListener('click', (event) => {
			const toggle = event.target.closest('.gallery-filter-group__toggle');
			if (!toggle || !this.filtersContainer.contains(toggle)) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const group = toggle.closest('.gallery-filter-group');
			if (!group) {
				return;
			}

			this.flyoutRoot = ensureFilterFlyoutRoot(this.blockId);

			if (group.classList.contains('is-filter-open')) {
				closeFilterDropdown(this.filtersContainer, this.flyoutRoot);
			} else {
				openFilterDropdownForGroup(group, this.blockId);
			}
		});

		this.onFilterPanelOutsideClick = (event) => {
			const group = getOpenFilterGroup(this.filtersContainer);
			if (!group) {
				return;
			}

			const target = event.target;
			const panel = getFilterPanel(group);

			if (group.contains(target) || panel?.contains(target)) {
				return;
			}

			closeFilterDropdown(this.filtersContainer, this.flyoutRoot);
		};

		document.addEventListener('click', this.onFilterPanelOutsideClick);

		this.onRepositionOpenPanels = () => {
			if (this.filterPanelRepositionFrame) {
				cancelAnimationFrame(this.filterPanelRepositionFrame);
			}

			this.filterPanelRepositionFrame = requestAnimationFrame(() => {
				this.filterPanelRepositionFrame = 0;
				repositionOpenFilterDropdown(
					this.filtersContainer,
					this.blockId,
					this.flyoutRoot
				);
			});
		};

		window.addEventListener('resize', this.onRepositionOpenPanels);
		window.addEventListener('scroll', this.onRepositionOpenPanels, true);

		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', this.onRepositionOpenPanels);
			window.visualViewport.addEventListener('scroll', this.onRepositionOpenPanels);
		}
	}

	/**
	 * Update active filter styles; close any open subcategory flyout.
	 *
	 * @param {HTMLButtonElement} activeBtn
	 */
	setActiveFilter(activeBtn) {
		closeFilterDropdown(
			this.filtersContainer,
			this.flyoutRoot || ensureFilterFlyoutRoot(this.blockId)
		);

		this.filtersContainer.querySelectorAll('.gallery-filter-btn').forEach((btn) => {
			btn.classList.remove('active');
		});

		this.filtersContainer.querySelectorAll('.gallery-filter-group').forEach((group) => {
			group.classList.remove('has-active-child');
		});

		activeBtn.classList.add('active');

		const parentSlug = activeBtn.dataset.parentCategory;
		if (parentSlug) {
			const group = this.filtersContainer.querySelector(
				`.gallery-filter-group[data-filter-group="${parentSlug}"]`
			);
			if (group) {
				group.classList.add('has-active-child');
			}
		}

	}

	/**
	 * Filter gallery items by category with smooth grid reflow animation
	 */
	filterGallery(category) {
		this.currentFilter = category;
		this.updateVisibleImages();

		this.items.forEach(item => {
			const itemCategories = item.dataset.categories || 'all';
			const categoriesArray = itemCategories.split(',').map(cat => cat.trim());
			const shouldShow = category === 'all' || categoriesArray.includes(category);
			
			if (shouldShow) {
				item.classList.remove('is-hidden');
				item.style.display = '';
			} else {
				item.classList.add('is-hidden');
				setTimeout(() => {
					if (item.classList.contains('is-hidden')) {
						item.style.display = 'none';
						this.applyPattern();
					}
				}, 300);
			}
		});

		this.applyPattern();

		if (!this.grid.classList.contains('is-revealed')) {
			this.revealGrid();
		}
	}

	/**
	 * Attach gallery item click listeners
	 */
	attachGalleryListeners() {
		this.items.forEach((item, index) => {
			const trigger = item.querySelector('.gallery-item__trigger');
			if (trigger) {
				trigger.addEventListener('click', () => {
					this.openLightbox(index);
				});
			}
		});
	}

	/**
	 * Attach lightbox control listeners
	 */
	attachLightboxListeners() {
		if (!this.lightbox) return;

		// Close button
		const closeBtn = this.lightbox.querySelector('.gallery-lightbox__close');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => this.closeLightbox());
		}

		// Overlay click to close
		const overlay = this.lightbox.querySelector('.gallery-lightbox__overlay');
		if (overlay) {
			overlay.addEventListener('click', () => this.closeLightbox());
		}

		// Navigation buttons
		const prevBtn = this.lightbox.querySelector('.gallery-lightbox__nav--prev');
		const nextBtn = this.lightbox.querySelector('.gallery-lightbox__nav--next');
		
		if (prevBtn) {
			prevBtn.addEventListener('click', () => this.navigateLightbox(-1));
		}
		
		if (nextBtn) {
			nextBtn.addEventListener('click', () => this.navigateLightbox(1));
		}

		// Touch/swipe support
		this.attachSwipeListeners();
	}

	/**
	 * Attach keyboard listeners for lightbox navigation
	 */
	attachKeyboardListeners() {
		document.addEventListener('keydown', (e) => {
			if (!this.lightbox.classList.contains('is-open')) return;

			switch (e.key) {
				case 'Escape':
					this.closeLightbox();
					break;
				case 'ArrowLeft':
					this.navigateLightbox(-1);
					break;
				case 'ArrowRight':
					this.navigateLightbox(1);
					break;
			}
		});
	}

	/**
	 * Attach swipe listeners for mobile
	 */
	attachSwipeListeners() {
		let touchStartX = 0;
		let touchEndX = 0;

		const container = this.lightbox.querySelector('.gallery-lightbox__container');
		if (!container) return;

		container.addEventListener('touchstart', (e) => {
			touchStartX = e.changedTouches[0].screenX;
		}, { passive: true });

		container.addEventListener('touchend', (e) => {
			touchEndX = e.changedTouches[0].screenX;
			this.handleSwipe(touchStartX, touchEndX);
		}, { passive: true });
	}

	/**
	 * Handle swipe gesture
	 */
	handleSwipe(startX, endX) {
		const diff = startX - endX;
		const threshold = 50;

		if (Math.abs(diff) > threshold) {
			if (diff > 0) {
				// Swipe left - next image
				this.navigateLightbox(1);
			} else {
				// Swipe right - previous image
				this.navigateLightbox(-1);
			}
		}
	}

	/**
	 * Open lightbox at specific image index
	 */
	openLightbox(index) {
		// Convert absolute index to filtered index if needed
		const filteredIndex = this.visibleImages.indexOf(index);
		if (filteredIndex === -1) {
			// Image not in current filter, open anyway at absolute index
			this.currentIndex = index;
		} else {
			this.currentIndex = filteredIndex;
		}

		this.showLightboxImage();
		this.lightbox.classList.add('is-open');
		this.lightbox.setAttribute('aria-hidden', 'false');
		document.body.style.overflow = 'hidden';

		// Focus trap
		this.lightbox.querySelector('.gallery-lightbox__close')?.focus();
	}

	/**
	 * Close lightbox
	 */
	closeLightbox() {
		this.lightbox.classList.remove('is-open');
		this.lightbox.setAttribute('aria-hidden', 'true');
		document.body.style.overflow = '';
	}

	/**
	 * Navigate lightbox (prev/next)
	 */
	navigateLightbox(direction) {
		const imageCount = this.visibleImages.length || this.galleryData.images.length;
		
		this.currentIndex += direction;
		
		// Loop around
		if (this.currentIndex < 0) {
			this.currentIndex = imageCount - 1;
		} else if (this.currentIndex >= imageCount) {
			this.currentIndex = 0;
		}

		this.showLightboxImage();
	}

	/**
	 * Show image in lightbox
	 */
	showLightboxImage() {
		// Get the actual image index from visible images or use current index
		const actualIndex = this.visibleImages.length > 0 
			? this.visibleImages[this.currentIndex]
			: this.currentIndex;

		const image = this.galleryData.images[actualIndex];
		if (!image) return;
		
		// Handle both old single category and new multiple categories format
		const imageCategories = Array.isArray(image.categories) ? image.categories : 
								(image.category ? [image.category] : ['all']);

		const imgElement = this.lightbox.querySelector('.gallery-lightbox__image');
		const captionElement = this.lightbox.querySelector('.gallery-lightbox__caption');
		const currentElement = this.lightbox.querySelector('.gallery-lightbox__current');
		const totalElement = this.lightbox.querySelector('.gallery-lightbox__total');

		if (imgElement) {
			imgElement.src = image.full;
			imgElement.alt = image.alt;
		}

		if (captionElement) {
			captionElement.textContent = image.caption || '';
			captionElement.style.display = image.caption ? 'block' : 'none';
		}

		if (currentElement) {
			currentElement.textContent = this.currentIndex + 1;
		}

		if (totalElement) {
			const total = this.visibleImages.length || this.galleryData.images.length;
			totalElement.textContent = total;
		}

		// Update navigation button states
		this.updateNavigationState();
	}

	/**
	 * Update navigation button states
	 */
	updateNavigationState() {
		const imageCount = this.visibleImages.length || this.galleryData.images.length;
		const prevBtn = this.lightbox.querySelector('.gallery-lightbox__nav--prev');
		const nextBtn = this.lightbox.querySelector('.gallery-lightbox__nav--next');

		// For single image, hide navigation
		if (imageCount <= 1) {
			if (prevBtn) prevBtn.style.display = 'none';
			if (nextBtn) nextBtn.style.display = 'none';
		} else {
			if (prevBtn) prevBtn.style.display = '';
			if (nextBtn) nextBtn.style.display = '';
		}
	}
}

/**
 * Initialize all gallery blocks on the page
 */
function initGalleryBlocks() {
	const galleryBlocks = document.querySelectorAll('.block-gallery');
	galleryBlocks.forEach(block => {
		new GalleryBlock(block);
	});
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initGalleryBlocks);
} else {
	initGalleryBlocks();
}

// Re-initialize on dynamic content load (e.g., AJAX)
document.addEventListener('gallery:reinit', initGalleryBlocks);

export default GalleryBlock;
